import { Routes } from '@angular/router';

import {
  authGuard,
  firstLoginOnlyGuard,
  redirectToDashboardIfLoggedInGuard,
} from './core/guards/auth.guard';
import { anyPermissionGuard, permissionGuard } from './core/guards/permission.guard';
import { MATERIAL_HANDLING_ROUTES } from './features/material-handling/material-handling.routes';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: 'login',
    title: 'Login',
    canActivate: [redirectToDashboardIfLoggedInGuard],
    loadComponent: () => import('./authenticate/login/login').then((m) => m.Login),
  },
  {
    path: 'forgot-password',
    title: 'Forgot password',
    canActivate: [redirectToDashboardIfLoggedInGuard],
    loadComponent: () =>
      import('./authenticate/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    path: 'reset-password',
    title: 'Reset password',
    canActivate: [redirectToDashboardIfLoggedInGuard],
    loadComponent: () =>
      import('./authenticate/reset-password/reset-password').then((m) => m.ResetPassword),
  },

  {
    path: 'force-reset-password',
    title: 'Reset password',
    canActivate: [firstLoginOnlyGuard],
    loadComponent: () =>
      import('./authenticate/force-reset-password/force-reset-password').then(
        (m) => m.ForceResetPassword,
      ),
  },
  {
    path: 'dashboard',
    title: 'Dashboard',
    canActivate: [authGuard],
    loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
  },
  {
    path: 'dynamic-dashboard',
    title: 'Dynamic Dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/dashboard/dynamic-dashboard/dynamic-dashboard').then(
        (m) => m.DynamicDashboard,
      ),
  },
  {
    path: 'users',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/user-management/users.routes').then((m) => m.USERS_ROUTES),
  },
  {
    path: 'roles',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/role-management/roles.routes').then((m) => m.ROLES_ROUTES),
  },
  {
    path: 'permissions',
    title: 'Permissions',
    canActivate: [authGuard],
    loadComponent: () => import('./features/permissions/permissions').then((m) => m.Permissions),
  },
  {
    path: 'menu-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/menu-management/menu.routes').then((m) => m.MENU_MANAGEMENT_ROUTES),
  },
  {
    path: 'configurations',
    title: 'Configurations',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/configurations/system-configurations/system-configurations').then(
        (m) => m.SystemConfigurations,
      ),
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/profile-management/profile.routes').then((m) => m.PROFILE_ROUTES),
  },
  {
    path: 'ppl-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/ppl-management/ppl-management.routes').then(
        (m) => m.PPL_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'product-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/product-management/product-management.routes').then(
        (m) => m.PRODUCT_MANAGEMENT_ROUTES,
      ),
  },
  ...MATERIAL_HANDLING_ROUTES,
  {
    path: 'shows-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/shows-management/shows-management.routes').then(
        (m) => m.SHOWS_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'cities',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/cities-management/cities-management.routes').then(
        (m) => m.CITIES_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'settings/audit-log',
    title: 'Audit Log',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/settings/audit-logs/audit-logs').then((m) => m.AuditLogs),
  },
  {
    path: 'settings/integrations',
    title: 'Integrations',
    canActivate: [
      authGuard,
      anyPermissionGuard(['social-tables.view', 'quickbooks-connection.view']),
    ],
    loadComponent: () =>
      import('./features/settings/integrations/integrations').then((m) => m.Integrations),
  },

  {
    path: 'pricing-tier-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/pricing-tier-management/pricing-tier-management.routes').then(
        (m) => m.PRICING_TIER_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'dynamic-forms',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/dynamic-form-management/dynamic-form-management.routes').then(
        (m) => m.DYNAMIC_FORM_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'exhibitor-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/exhibitor-management/exhibitor-management.routes').then(
        (m) => m.EXHIBITOR_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'cart-order-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/cart-order-management/cart-order-management.routes').then(
        (m) => m.CART_ORDER_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'order-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/order-management/order-management.routes').then(
        (m) => m.ORDER_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'contract-templates',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/contract-templates/contract-templates.routes').then(
        (m) => m.CONTRACT_TEMPLATES_ROUTES,
      ),
  },
  {
    path: 'promotional/gift-certificates',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/gift-certificate-management/gift-certificate-management.routes').then(
        (m) => m.GIFT_CERTIFICATE_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'business-card-exchange',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/business-card-exchange/business-card-exchange.routes').then(
        (m) => m.BUSINESS_CARD_EXCHANGE_ROUTES,
      ),
  },
  {
    // Website Banner Advertising (Epic 90): each child route carries its own permission guard.
    path: 'banner-advertising',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/banner-advertising/banner-advertising.routes').then(
        (m) => m.BANNER_ADVERTISING_ROUTES,
      ),
  },
  {
    path: 'promotional/coupon-codes',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/coupon-management/coupon-management.routes').then(
        (m) => m.COUPON_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'promotional/coupon-usage-reports',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/coupon-management/coupon-usage-report.routes').then(
        (m) => m.COUPON_USAGE_REPORT_ROUTES,
      ),
  },
  {
    path: 'boards',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/board-dashboards/board-dashboards.routes').then(
        (m) => m.BOARD_DASHBOARDS_ROUTES,
      ),
  },
  {
    path: 'industries',
    title: 'Industries',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/industries-management/industries-management').then(
        (m) => m.IndustriesManagement,
      ),
  },
  {
    path: 'notifications',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/notifications-management/notifications-management.routes').then(
        (m) => m.NOTIFICATIONS_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'announcements',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/announcements/announcements.routes').then((m) => m.ANNOUNCEMENTS_ROUTES),
  },
  {
    path: 'post-mortem-reports',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/post-mortem-reports/post-mortem-reports.routes').then(
        (m) => m.POST_MORTEM_REPORTS_ROUTES,
      ),
  },
  {
    path: 'incident-reports',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/incident-reports/incident-reports.routes').then(
        (m) => m.INCIDENT_REPORTS_ROUTES,
      ),
  },
  {
    path: 'reports/cog',
    loadChildren: () =>
      import('./features/cog-report/cog-report.routes').then((m) => m.COG_REPORT_ROUTES),
  },
  {
    // Public, password-protected share link of a COG Report year (epic 87.8). No session needed.
    path: 'shared-cog-reports/:token',
    title: 'Shared COG Report',
    loadComponent: () =>
      import('./features/cog-report/shared-cog-report/shared-cog-report').then(
        (m) => m.SharedCogReport,
      ),
  },
  {
    path: 'agenda',
    canActivate: [authGuard, permissionGuard('agenda.read')],
    loadChildren: () =>
      import('./features/agenda-management/agenda.routes').then((m) => m.AGENDA_ROUTES),
  },
  {
    path: 'production-checklist',
    canActivate: [authGuard, permissionGuard('production-checklist.read')],
    loadChildren: () =>
      import('./features/production-checklist/production-checklist.routes').then(
        (m) => m.PRODUCTION_CHECKLIST_ROUTES,
      ),
  },
  {
    path: 'speaker-submissions',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/speaker-submissions/speaker-submissions.routes').then(
        (m) => m.SPEAKER_SUBMISSIONS_ROUTES,
      ),
  },
  {
    path: 'calendar-management',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/calendar-management/calendar-management.routes').then(
        (m) => m.CALENDAR_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: '404',
    title: 'Page not found',
    loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
